import { Flex, Button, Input, Modal } from "antd";
import { DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useEditProductContext } from "../../../contexts/edit-product-context/useEditProductContext";
import { PriceInput } from "../../atoms/PriceInput";
import { v4 as uuidv4 } from "uuid";
import { Section } from "../../pages/EditProduct";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBranchContext } from "../../../contexts/branch-context";

export const VariantsList: React.FC = () => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const {
    setUnsavedChanges,
    productBaseVariants,
    setProductBaseVariants,
  } = useEditProductContext();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasSingleVariant = productBaseVariants.length === 1;

  // Don't render anything for single variant - it's handled in ProductMainData
  if (hasSingleVariant) {
    return null;
  }

  return (
    <Section
      title={
        <Flex align="center" gap={8}>
          <span>{t("variantsList.sectionTitle")}</span>
          <Button
            type="link"
            icon={<QuestionCircleOutlined />}
            onClick={() => setIsModalOpen(true)}
            style={{ padding: 0, height: "auto" }}
          >
            {t("variantsList.whatAreVariantsLink")}
          </Button>
        </Flex>
      }
    >
      {productBaseVariants.sort((a: any, b: any) => a.id - b.id).map((variant, index) => {
        return (
          <Flex
            justify="space-between"
            align="center"
            style={{
              marginBottom: 20,
            }}
          >
            <Flex justify="space-between" gap={20} align="center">
              <Input
                value={variant.name}
                onChange={(e) => {
                  setUnsavedChanges(true);
                  setProductBaseVariants((current: any) => {
                    const newArray = current;
                    newArray[index].name = e.target.value;
                    return [...newArray];
                  });
                }}
              />
              <PriceInput
                value={variant.price}
                onChange={(price) => {
                  setUnsavedChanges(true);
                  setProductBaseVariants((current: any) => {
                    const newArray = current;
                    newArray[index].price = price;
                    return [...newArray];
                  });
                }}
                editMode={true}
                width="300px"
                currency={currentBranch?.currency}
              />
            </Flex>
            {productBaseVariants.length > 1 && (
              <Button
                shape="circle"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setUnsavedChanges(true);
                  setProductBaseVariants((current: any) => {
                    const newArray = current;
                    newArray.splice(index, 1);
                    return [...newArray];
                  });
                }}
              />
            )}
          </Flex>
        );
      })}
      <Button
        icon={<PlusOutlined />}
        onClick={() => {
          setUnsavedChanges(true);
          setProductBaseVariants((current: any) => {
            const newArray = current;
            newArray.push({
              id: "NEW_VARIANT-" + uuidv4(),
              key: uuidv4(),
              name: "",
              price: 0,
            });
            return [...newArray];
          });
        }}
      >
        {t("variantsList.addVariant")}
      </Button>

      <Modal
        title={t("variantsList.modalTitle")}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsModalOpen(false)}>
            {t("variantsList.modalCloseButton")}
          </Button>,
        ]}
        width={600}
      >
        <div style={{ lineHeight: 1.8 }}>
          <p>
            <strong>{t("variantsList.modalIntroBold")}</strong> {t("variantsList.modalIntroText")}
          </p>

          <h4>{t("variantsList.modalExamplesHeading")}</h4>

          <div style={{ marginBottom: 16 }}>
            <strong>{t("variantsList.modalPizzaLabel")}</strong>
            <ul>
              <li>{t("variantsList.modalPizzaSmall")}</li>
              <li>{t("variantsList.modalPizzaMedium")}</li>
              <li>{t("variantsList.modalPizzaLarge")}</li>
            </ul>
          </div>

          <div style={{ marginBottom: 16 }}>
            <strong>{t("variantsList.modalCoffeeLabel")}</strong>
            <ul>
              <li>{t("variantsList.modalCoffeeEspresso")}</li>
              <li>{t("variantsList.modalCoffeeDoubleEspresso")}</li>
              <li>{t("variantsList.modalCoffeeAmericano")}</li>
            </ul>
          </div>

          <div style={{ marginBottom: 16 }}>
            <strong>{t("variantsList.modalDrinksLabel")}</strong>
            <ul>
              <li>{t("variantsList.modalDrinksSmall")}</li>
              <li>{t("variantsList.modalDrinksMedium")}</li>
              <li>{t("variantsList.modalDrinksLarge")}</li>
            </ul>
          </div>

          <p style={{ marginTop: 16, color: "var(--color-text-secondary)" }}>
            <strong>{t("variantsList.modalTipBold")}</strong> {t("variantsList.modalTipText")}
          </p>
        </div>
      </Modal>
    </Section>
  );
};
